import { useState, useEffect, useCallback } from "react";
import { nomesService, enumsService, distribuicaoService } from "../../../services/api";
import { formatCpfCnpj } from "../../../utils/formatCpfCnpj";

const TIPO_CONSULTA_LABELS = {
  PESSOA: "Pessoa física ou jurídica",
  ADVOGADO: "Advogado",
  ASSUNTO: "Assunto",
  CLASSE_PROCESSUAL: "Classe Processual",
  DOCUMENTO: "Documento (CPF/CNPJ)",
  OAB: "OAB de Advogado",
};

/**
 * Hook personalizado para gerenciar o formulário de cadastro de nomes
 */
export const useNomeForm = (onSuccess) => {
  // Estado para modal e dados de cadastro
  const [showAddModal, setShowAddModal] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [poloOptions, setPoloOptions] = useState([]);
  const [statusOptionsCadastro, setStatusOptionsCadastro] = useState([]);
  const [abrangencias, setAbrangencias] = useState([]);
  const [tipoConsultaOptions, setTipoConsultaOptions] = useState([]);
  
  // Estado para o formulário
  const [novoNome, setNovoNome] = useState({
    id_arrendatario: 0,
    id_solucionare: 0,
    nome: '',
    nome_exato: true,
    cpf_cnpj: '',
    oab: '',
    oab_uf: '',
    cod_tipo_consulta: 1,
    documentos: '',
    multiplos_cnpjs: false,
    polo: 'Passivo',
    obter_anexos: true,
    tribunais_especificos: false,
    lista_tribunais: [],
    instancias: "[1,2]",
    status: 'ATIVO',
  });

  // Buscar polos, status e tipos de consulta ao abrir modal
  const fetchPoloStatus = useCallback(async () => {
    try {
      const polos = await enumsService.buscarPolo();
      setPoloOptions(Object.values(polos));
      const status = await enumsService.buscarNomeStatus();
      setStatusOptionsCadastro(Object.values(status));
      const tipos = await enumsService.buscarTipoConsulta();
      const tiposFormatados = Object.entries(tipos).map(([key, value]) => ({
        value,
        label: TIPO_CONSULTA_LABELS[key] || key,
      }));
      setTipoConsultaOptions(tiposFormatados);
    } catch (error) {
      alert('Erro ao buscar polos ou status: ' + error.message);
    }
  }, []);

  // Buscar abrangências ao abrir modal
  const fetchAbrangencias = useCallback(async () => {
    try {
      const abrang = await distribuicaoService.buscarDistribuicaoAbrangencia();
      setAbrangencias(abrang);
      // Seleciona todas por padrão ao abrir modal
      setNovoNome(prev => ({ ...prev, lista_tribunais: abrang }));
    } catch (error) {
      alert('Erro ao buscar abrangências: ' + error.message);
    }
  }, []);

  // Carregar dados quando o modal é aberto
  useEffect(() => {
    if (showAddModal) {
      fetchPoloStatus();
      fetchAbrangencias();
    }
  }, [showAddModal, fetchPoloStatus, fetchAbrangencias]);

  // Handler para alterar campos do novo nome
  const handleNovoNomeChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'lista_tribunais') {
      // Não usado mais, pois checkboxes lidam com seleção
      return;
    }

    let newValue = type === 'checkbox' ? checked : value;
    if (name === 'cpf_cnpj') {
      newValue = formatCpfCnpj(newValue);
    }
    if (name === 'cod_tipo_consulta') {
      newValue = parseInt(value, 10);
    }

    setNovoNome(prev => {
      const updates = { [name]: newValue };

      // Ao marcar múltiplos CNPJs, oculta e limpa o campo único de CPF/CNPJ
      if (name === 'multiplos_cnpjs' && newValue === true) {
        updates.cpf_cnpj = '';
      }
      // Ao desmarcar múltiplos CNPJs, oculta e limpa o campo de documentos
      if (name === 'multiplos_cnpjs' && newValue === false) {
        updates.documentos = '';
      }
      // Ao alterar o tipo de consulta, reseta o flag de múltiplos CNPJs
      if (name === 'cod_tipo_consulta' && newValue !== 5) {
        updates.multiplos_cnpjs = false;
        updates.documentos = '';
      }

      // Ao alterar o tipo de consulta para 5 ou 6, desmarca o nome exato
      if (name === 'cod_tipo_consulta' && (newValue === 5 || newValue === 6)) {
        updates.nome_exato = false;
      }

      return { ...prev, ...updates };
    });
  };

  // Helpers para visibilidade de campos por tipo de consulta
  const tipoConsulta = novoNome.cod_tipo_consulta;
  const ehTipoDocumento = tipoConsulta === 5;
  const multiplosCnpjsAtivo = novoNome.multiplos_cnpjs === true;
  const mostrarCampoNome = true; // Nome sempre visível
  const mostrarCampoCpfCnpj = tipoConsulta === 1 || (ehTipoDocumento && !multiplosCnpjsAtivo);
  const mostrarCampoOab = tipoConsulta === 2 || tipoConsulta === 6;
  const mostrarCampoDocumentos = ehTipoDocumento && multiplosCnpjsAtivo;

  // Handler específico para checkboxes de abrangências
  const handleAbrangenciaChange = (abr) => {
    setNovoNome(prev => {
      const lista = prev.lista_tribunais || [];
      if (lista.includes(abr)) {
        return { ...prev, lista_tribunais: lista.filter(a => a !== abr) };
      } else {
        return { ...prev, lista_tribunais: [...lista, abr] };
      }
    });
  };

  // Handler para submeter novo nome
  const handleAddNome = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    try {
      let documentos = novoNome.documentos;
      if (novoNome.cod_tipo_consulta === 5 && documentos) {
        documentos = JSON.stringify(
          documentos
            .split(/\r?\n/)
            .map((doc) => doc.trim())
            .filter((doc) => doc.length > 0)
        );
      }

      const payload = {
        ...novoNome,
        documentos,
        instancias: JSON.stringify([1,2]),
        lista_tribunais: JSON.stringify(novoNome.lista_tribunais),
        status: "ATIVO",
      };
      await nomesService.adicionarNome(payload);
      setShowAddModal(false);
      resetForm();
      // Chama o callback de sucesso para atualizar a lista
      if (onSuccess) onSuccess();
    } catch (error) {
      let errorMsg = error.message;
      try {
        // Verifica se a resposta tem um corpo JSON com campo detail
        if (error.response && error.response.data) {
          const responseData = error.response.data;
          if (responseData.detail) {
            errorMsg = responseData.detail;
          }
        }
      } catch (jsonError) {
        // Se houver erro ao processar o JSON, mantém a mensagem original
        console.error("Erro ao processar resposta JSON:", jsonError);
      }
      alert('Erro ao adicionar nome: ' + errorMsg);
    } finally {
      setAddLoading(false);
    }
  };

  // Resetar formulário
  const resetForm = () => {
    setNovoNome({
      id_arrendatario: 0,
      id_solucionare: 0,
      nome: '',
      nome_exato: true,
      cpf_cnpj: '',
      oab: '',
      oab_uf: '',
      cod_tipo_consulta: 1,
      documentos: '',
      multiplos_cnpjs: false,
      polo: 'Passivo',
      obter_anexos: true,
      tribunais_especificos: false,
      lista_tribunais: [],
      instancias: "[1,2]",
      status: 'ATIVO',
    });
  };

  // Abrir modal
  const openModal = () => {
    setShowAddModal(true);
  };

  // Fechar modal
  const closeModal = () => {
    setShowAddModal(false);
    resetForm();
  };

  return {
    // Estados
    showAddModal,
    addLoading,
    novoNome,
    poloOptions,
    statusOptionsCadastro,
    abrangencias,
    tipoConsultaOptions,
    // Visibilidade por tipo de consulta
    mostrarCampoNome,
    mostrarCampoCpfCnpj,
    mostrarCampoOab,
    mostrarCampoDocumentos,
    
    // Ações
    handleNovoNomeChange,
    handleAbrangenciaChange,
    handleAddNome,
    openModal,
    closeModal,
  };
};
